/**
 * How much of a PO line has been delivered — ONE answer, for every surface.
 *
 * CR-7417 found the grid row reading "Qty Delivered 61" beside "100% Delivered"
 * on the reported line (180 ordered, 180 arrived, 119 rejected on eight NCRs).
 * The quantity had been corrected onto the accepted figure and the percentage
 * left on `received_qty`, so the same row contradicted itself. It was fixed by
 * moving the percentage onto accepted as well, and by collapsing the three
 * hand-written copies — the grid cell, the grid's sort comparator and the modal
 * header pill, two of which rounded differently — onto one function.
 *
 * CR-7468 gives each figure its own field on every screen: Delivered is what
 * arrived, Accepted is what was kept, and the shortfall between them is Rejected.
 * With the two separated, Aaron ruled the percentage back onto the gross figure
 * (22 Sep 2026): *"it should be the percentage delivered just like it says it
 * is"*. The reported line now reads "Qty Delivered 180" beside "100% Delivered"
 * — which agree, because both answer the same question — and "Qty Accepted 61"
 * in the cell that owns the other one.
 *
 * The source is the RECEIPTS, never the stored `received_qty` column. The feed's
 * figure is not a delivery the platform can evidence (CR-7468 scope A1), and
 * `delivered_qty` is the receipt-derived number the Qty Delivered cell renders,
 * so the pill and the cell cannot diverge.
 *
 * `barPercent` is the rounding, because it is the one with a rule written down:
 * it floors, so 100% can only appear when the whole line has genuinely arrived.
 */
import type { PurchaseOrderLine } from '../../lib/api';
import { barPercent } from './tabs/po-lines/ProgressCell';

/**
 * Parse one quantity field, keeping NULL distinct from zero.
 *
 * NULL is not zero, and the distinction is the whole point of this work. A
 * frozen row captured before the snapshot held the quantity carries `null`, and
 * the cell beside this one already renders a dash for it (`formatQuantity`
 * returns '-' for null). Collapsing it to 0 put "Qty Delivered -" next to a
 * confident "0% Delivered" on the same row — the self-contradicting row this
 * work exists to end, reintroduced one column across. Two independent reviewers
 * found it on the same pass, which is roughly how obvious it was in hindsight.
 */
function quantityOf(raw: string | null | undefined): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * The line's DELIVERED quantity — what the receipts say arrived, gross, before
 * anything was rejected — or NULL when there is no figure.
 */
export function deliveredQtyOf(
  line: Pick<PurchaseOrderLine, 'delivered_qty'>,
): number | null {
  return quantityOf(line.delivered_qty);
}

/**
 * The line's ACCEPTED quantity — what was kept, delivered less rejected — or
 * NULL when there is no figure. Same rule.
 *
 * Not what the percentage measures (CR-7468); this is the parser for the Qty
 * Accepted field, which owns that number on its own.
 */
export function acceptedQtyOf(
  line: Pick<PurchaseOrderLine, 'accepted_qty'>,
): number | null {
  return quantityOf(line.accepted_qty);
}

/** The ordered quantity, or NULL when there is no figure. Same rule. */
export function orderedQtyOf(
  line: Pick<PurchaseOrderLine, 'quantity'>,
): number | null {
  return quantityOf(line.quantity);
}

/**
 * Percentage of the line DELIVERED, 0-100 and floored — or NULL when either
 * quantity is missing, so the caller can render the same dash the cell beside
 * it renders instead of inventing a zero.
 *
 * Measures what arrived, not what was kept: the label says delivered, so the
 * number is delivered (Aaron, 22 Sep 2026). A line whose deliveries were all
 * rejected reads 100% here and 0 in its Qty Accepted cell, and those are two
 * true answers to two different questions rather than one row disagreeing with
 * itself.
 *
 * @param line - Any row carrying `delivered_qty` and `quantity`.
 * @returns An integer 0-100, or null when there is nothing to measure.
 */
export function deliveredPercentOf(
  line: Pick<PurchaseOrderLine, 'delivered_qty' | 'quantity'>,
): number | null {
  const delivered = deliveredQtyOf(line);
  const ordered = orderedQtyOf(line);
  if (delivered === null || ordered === null) return null;
  return barPercent(delivered, ordered);
}
